import { Injectable } from '@angular/core';
import { HttpService } from 'src/app/shared/service/http.service';
import { Observable } from 'rxjs';
import { Currency } from 'src/app/shared/models/currency.model';
import { map, tap } from 'rxjs/operators';
import { CurrencyFaqModel } from 'src/app/shared/models/faq.model';
import { Language } from '../models/language.type';
import { Network } from '../models/network.model';
import { PreListCurrencyChanges, PreListedCurrencyModel } from '../models/pre-listed-currency.model';

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  constructor(private http: HttpService) {
  }

  getCurrencies(): Observable<Currency[]> {
    return this.http.request('get', `/v2/admin/currency`).pipe(
      map((response: any) => response.data),
      map((currencies: any[]) => currencies.map((currency) => new Currency(currency)))
    );
  }

  updatePrelistCurrency(id: number, fieldName: string, value: any): Observable<PreListedCurrencyModel[]> {
    return this.http.request('post', `/v1/admin/pre-list/edit`, {
      id: id,
      fieldName,
      value
    });
  }

  getSimilarCurrencies() {
    return this.http.request('get', `/v1/admin/currency-single-page`);
  }

  updateRelatedCurrencies(form: any): Observable<PreListedCurrencyModel[]> {
    return this.http.request('post', `/v1/admin/currency-single-page`, form);
  }

  uploadIndexCurrencyVideo(form: FormData): Observable<PreListedCurrencyModel[]> {
    return this.http.request('post', `/v1/admin/currency-single-page`, form, 'multipart/form-data');
  }

  updatePrelistCurrencyImages(form: FormData): Observable<PreListedCurrencyModel[]> {
    return this.http.request('post', `/v1/admin/pre-list/edit`, form, 'multipart/form-data');
  }

  preListCurrency(currencyId: number): Observable<PreListedCurrencyModel[]> {
    return this.http.request('post', `/v1/admin/pre-list/submit`, {
      id: currencyId
    });
  }

  getPrelistCoinChanges(tokenId: number): Observable<PreListCurrencyChanges[]> {
    return this.http.request('get', `/v1/admin/pre-list/history?id=${tokenId}`).pipe(
      map((response: any) => response.data),
      map((currencies: any[]) => currencies.map((changes) => new PreListCurrencyChanges(changes)))
    );
  }

  getPrelistnCoin(): Observable<PreListedCurrencyModel[]> {
    return this.http.request('get', `/v1/admin/pre-list`).pipe(
      map((response: any) => response.data),
      map((currencies: any[]) => currencies.map((currency) => new PreListedCurrencyModel(currency)))
    );
  }

  prelistnNewCoin(coinSymbol: string): Observable<Currency[]> {
    return this.http.request('post', `/v1/admin/pre-list`, {
      currencyId: coinSymbol
    });
  }

  saveCurrency(
    currency: Currency,
    lang: Language = 'fa',
    title: string,
    mainTitle: string,
    metaDescription: string,
    summary: string,
    description?: string
  ): Observable<any> {
    const tempCurrency = Object.assign(new Currency(), currency);
    tempCurrency.rialToTooman();
    let data: any = tempCurrency;
    if (description) {
      data = { ...tempCurrency, description, title, mainTitle, metaDescription, summary };
    }
    return this.http.request('put', `/v1/admin/currency/${currency.id}?language=${lang}`, data);
  }

  saveCurrencySettings(currency: Currency, network: Network): Observable<any> {
    return this.http.request('put', `/v2/admin/currency/networks/fee`, {
      currency_id: currency.id,
      currency_token_id: network.currencyTokenId,
      withdraw_fee: network.withdrawFee,
      minimum_withdraw_amount: network.minimumWithdrawAmount,
      withdraw_enabled: network.withdrawEnabled,
      deposit_enabled: network.depositEnabled
    });
  }

  getFaqOfCurrency(id: string, lang: Language = 'fa'): Observable<CurrencyFaqModel[]> {
    return this.http
      .request('get', `/v1/admin/currency/${id}/faq?language=${lang}`)
      .pipe(map((response: any) => response.data));
  }

  saveFaq(faq: CurrencyFaqModel, currencyId: string, lang: Language = 'fa'): Observable<any> {
    if (faq.id) {
      return this.http.request('put', `/v1/admin/currency/faq/${faq.id}?language=${lang}`, faq);
    } else {
      return this.http.request(
        'post',
        `/v1/admin/currency/${currencyId}/faq?language=${lang}`,
        faq
      );
    }
  }

  deleteFaq(faq: CurrencyFaqModel): Observable<any> {
    return this.http.request('delete', `/v1/admin/currency/faq/${faq.id}`);
  }
}
